import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const CategoryForm = ({ formData, onChange, existingCategories }) => {
  const [errors, setErrors] = useState({});

  // Convert existing categories to Select options
  const categoryOptions = existingCategories
    .filter(cat => cat.level < 3) // Limit hierarchy depth
    .map(cat => ({
      value: cat.id,
      label: '  '.repeat(cat.level) + cat.name,
      description: `Level ${cat.level + 1}`
    }));

  const validateField = (field, value) => {
    let error = '';
    
    switch (field) {
      case 'name':
        if (!value?.trim()) {
          error = 'Category name is required';
        } else if (value.length < 2) {
          error = 'Category name must be at least 2 characters';
        } else if (value.length > 50) {
          error = 'Category name must not exceed 50 characters';
        } else {
          // Check for duplicate names at the same level
          const parentId = formData.parentCategory;
          const siblings = existingCategories?.filter(cat => cat.parent === parentId);
          const duplicate = siblings.some(cat => 
            cat.name.toLowerCase() === value.toLowerCase()
          );
          if (duplicate) {
            error = 'Category name already exists at this level';
          }
        }
        break;
      case 'slug':
        if (value && !/^[a-z0-9-]+$/.test(value)) {
          error = 'URL slug can only contain lowercase letters, numbers, and hyphens';
        }
        break;
      case 'description':
        if (value && value.length > 500) {
          error = 'Description must not exceed 500 characters';
        }
        break;
      case 'seoTitle':
        if (value && value.length > 60) {
          error = 'SEO title should not exceed 60 characters for optimal display';
        }
        break;
      case 'seoDescription':
        if (value && value.length > 160) {
          error = 'SEO description should not exceed 160 characters for optimal display';
        }
        break;
    }

    return error;
  };

  const handleFieldChange = (field, value) => {
    onChange(field, value);
    
    // Real-time validation
    const error = validateField(field, value);
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    handleFieldChange('name', value);
  };

  const handleSlugChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    handleFieldChange('slug', value);
  };

  const getSlugPreview = () => {
    if (!formData.slug) return '';
    return `${window.location.origin}/category/${formData.slug}`;
  };

  return (
    <div className="space-y-6">
      {/* Category Details Section */}
      <div className="bg-white rounded-lg border border-border p-6">
        <div className="flex items-center mb-6">
          <Icon name="Tag" size={20} className="text-primary mr-2" />
          <h2 className="text-xl font-semibold text-foreground">Category Details</h2>
        </div>

        <div className="space-y-6">
          <div>
            <Input
              label="Category Name"
              placeholder="Enter category name"
              value={formData.name || ''}
              onChange={handleNameChange}
              required
              error={errors.name}
              description="The display name for this category"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Category Description
              <span className="text-muted-foreground ml-2">
                ({(formData.description || '').length}/500)
              </span>
            </label>
            <textarea
              placeholder="Describe this category and what products it contains..."
              value={formData.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-none"
            />
            {errors.description && (
              <p className="text-sm text-destructive mt-1">{errors.description}</p>
            )}
          </div>

          <div>
            <Select
              label="Parent Category"
              placeholder="Select parent category (optional)"
              options={[
                { value: '', label: 'No parent (root level)', description: 'Top-level category' },
                ...categoryOptions
              ]}
              value={formData.parentCategory || ''}
              onChange={(value) => handleFieldChange('parentCategory', value)}
              searchable
              description="Choose a parent to create a subcategory"
            />
          </div>
        </div>
      </div>

      {/* URL & SEO Section */}
      <div className="bg-white rounded-lg border border-border p-6">
        <div className="flex items-center mb-6">
          <Icon name="Globe" size={20} className="text-primary mr-2" />
          <h2 className="text-xl font-semibold text-foreground">URL & SEO Settings</h2>
        </div>

        <div className="space-y-6">
          <div>
            <Input
              label="URL Slug"
              placeholder="category-url-slug"
              value={formData.slug || ''}
              onChange={handleSlugChange}
              error={errors.slug}
              description="Used in the category URL (auto-generated from name)"
            />
            {formData.slug && (
              <div className="mt-2 p-2 bg-muted rounded-md">
                <p className="text-xs text-muted-foreground">URL Preview:</p>
                <p className="text-sm text-primary break-all">{getSlugPreview()}</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                label="SEO Title"
                placeholder="Category name for search engines"
                value={formData.seoTitle || ''}
                onChange={(e) => handleFieldChange('seoTitle', e.target.value)}
                error={errors.seoTitle}
                description={`${(formData.seoTitle || '').length}/60 characters`}
              />
            </div>

            <div>
              <Input
                label="SEO Keywords"
                placeholder="keyword1, keyword2, keyword3"
                value={formData.seoKeywords || ''}
                onChange={(e) => handleFieldChange('seoKeywords', e.target.value)}
                description="Comma-separated keywords"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              SEO Description
              <span className="text-muted-foreground ml-2">
                ({(formData.seoDescription || '').length}/160)
              </span>
            </label>
            <textarea
              placeholder="Brief description that appears in search results"
              value={formData.seoDescription || ''}
              onChange={(e) => handleFieldChange('seoDescription', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-none"
            />
            {errors.seoDescription && (
              <p className="text-sm text-destructive mt-1">{errors.seoDescription}</p>
            )}
          </div>
        </div>
      </div>

      {/* Preview Section */}
      {formData.name && (
        <div className="bg-white rounded-lg border border-border p-6">
          <div className="flex items-center mb-4">
            <Icon name="Eye" size={20} className="text-primary mr-2" />
            <h3 className="text-lg font-semibold text-foreground">Category Preview</h3>
          </div>

          <div className="border rounded-lg p-4 bg-muted/50">
            <div className="flex items-center space-x-3 mb-3">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: formData.color }}
              >
                <Icon name="Folder" size={14} className="text-white" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">{formData.name}</h4>
                {formData.parentCategory && (
                  <p className="text-xs text-muted-foreground">
                    Under: {existingCategories.find(cat => cat.id === formData.parentCategory)?.name}
                  </p>
                )}
              </div>
            </div>
            {formData.description && (
              <p className="text-sm text-muted-foreground">{formData.description}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryForm;